import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import { LineChart as LineChartIcon } from 'lucide-react';

const StackedBarChart = ({
  title,
  xAxisData = [],
  yAxisData = [],
  barColors = [],
  onBarClick = () => {},
  jobMetrics = [],
}) => {
  const [isDark, setIsDark] = React.useState(
    document.documentElement.classList.contains('dark'),
  );

  React.useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);
  // Transform data for Recharts
  // Assumes xAxisData is an array of labels: ['Role1', 'Role2']
  // Assumes yAxisData is an array of series objects: [{ name: 'Sourced', data: [10, 20] }, { name: 'Uploads', data: [5, 10] }]
  // Transform data for Recharts
  // Assumes xAxisData is an array of labels: ['Role1', 'Role2']
  // Assumes yAxisData is an array of series objects: [{ name: 'Sourced', data: [10, 20] }, { name: 'Uploads', data: [5, 10] }]

  const processedData = xAxisData.map((label, index) => {
    const matchingMetric = jobMetrics && jobMetrics[index];
    const item = {
      // name: label.length > 20 ? label.slice(0, 21) + '…' : label,
      name: label,
      job_id: matchingMetric?.job_id || null,
      job_role: label,
    };
    yAxisData.forEach((series) => {
      item[series.name] = series.data[index] || 0;
    });
    return item;
  });

  const hasData =
    xAxisData.length > 0 &&
    yAxisData.some(
      (series) => series.data && series.data.some((val) => val > 0),
    );

  const keys = yAxisData.map((s) => s.name);

  // Dummy data for empty state
  const dummyData = [
    { name: 'Role A', Sourced: 45, Uploads: 30 },
    { name: 'Role B', Sourced: 55, Uploads: 40 },
    { name: 'Role C', Sourced: 35, Uploads: 25 },
    { name: 'Role D', Sourced: 60, Uploads: 45 },
    { name: 'Role E', Sourced: 40, Uploads: 20 },
    { name: 'Role F', Sourced: 50, Uploads: 35 },
  ];
  const dummyKeys = ['Sourced', 'Uploads'];
  const dummyColors = ['#9CA3AF', '#D1D5DB'];

  const chartData = hasData ? processedData : dummyData;
  const chartKeys = hasData ? keys : dummyKeys;
  const chartColors = hasData && barColors.length > 0 ? barColors : dummyColors;
  const isScrollable = chartData.length > 8;

  // Compute the max stacked total for custom x-axis ticks
  const maxTotal = Math.max(
    ...chartData.map((item) =>
      chartKeys.reduce((sum, key) => sum + (item[key] || 0), 0)
    ),
    1
  );

  // Generate nice tick values (e.g. 0, 10, 20, 30...)
  const generateTicks = (max) => {
    if (max <= 0) return [0];
    const rawStep = max / 5;
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const residual = rawStep / magnitude;
    let niceStep;
    if (residual <= 1) niceStep = 1 * magnitude;
    else if (residual <= 2) niceStep = 2 * magnitude;
    else if (residual <= 5) niceStep = 5 * magnitude;
    else niceStep = 10 * magnitude;
    const ticks = [];
    for (let t = 0; t <= max + niceStep; t += niceStep) {
      ticks.push(t);
      if (t >= max) break;
    }
    return ticks;
  };

  const customTicks = generateTicks(maxTotal);
  const customMax = customTicks[customTicks.length - 1];

  const Y_AXIS_WIDTH = 160; // Maximum width for job role name labels

  // const StackBottomLabel = ({ x, width, value }) => {
  //   const words = String(value).split(' ');
  //   return (
  //     <text
  //       x={x + width / 2} // center of bar
  //       y={CHART_HEIGHT - MARGIN_BOTTOM + LABEL_OFFSET} // SAME Y FOR ALL
  //       textAnchor="middle"
  //       fill={isDark ? '#AEB9E1' : '#707070'}
  //       fontSize={10}
  //     >
  //       {words.map((word, index) => (
  //         <tspan
  //           key={index}
  //           x={x + width / 2} // keep each line centered
  //           dy={index === 0 ? 0 : 14} // move next line down
  //         >
  //           {word}
  //         </tspan>
  //       ))}
  //     </text>
  //   );
  // };

  const CustomYAxisTick = ({ x, y, payload }) => {
    const text = String(payload.value);
    const words = text.split(' ');

    // Handle single long word
    if (words.length === 1 && text.length > 30) {
      return (
        <text
          x={x - 6}
          y={y}
          textAnchor="end"
          fill={isDark ? '#AEB9E1' : '#707070'}
          fontSize={14}   // 🔥 Increased font size
        >
          <tspan x={x - 6} dy={secondLineText ? -4 : 4}>{t1}</tspan>
          {secondLineText && (
            <tspan x={x - 6} dy={14}>{t2}</tspan>
          )}
        </text>
      );
    }


    // Split logic: If total length <= 18, use 1 line. Otherwise, try to split at a space near the middle or the 18 mark.
    let firstLineText = text;
    let secondLineText = "";

    if (text.length > 18) {
      // Find space closest to the middle around 18 characters
      const mid = Math.floor(text.length / 2);
      const spaceIndex = text.lastIndexOf(' ', Math.min(text.length, 22)); // look for last space before 22 chars
      
      if (spaceIndex !== -1 && spaceIndex > 4) {
        firstLineText = text.slice(0, spaceIndex);
        secondLineText = text.slice(spaceIndex + 1);
      } else if (words.length > 2) {
        // Fallback to word splitting if no space found near the limit
        firstLineText = words.slice(0, 2).join(' ');
        secondLineText = words.slice(2).join(' ');
      }
    }

    // Truncate if still too long for our reduced Y_AXIS_WIDTH (approx 20-22 chars)
    const t1 = firstLineText.length > 24 ? firstLineText.slice(0, 21) + '...' : firstLineText;
    const t2 = secondLineText.length > 24 ? secondLineText.slice(0, 21) + '...' : secondLineText;

    return (
      <text
        x={x - 6}
        y={y}
        textAnchor="end"
        fill={isDark ? '#AEB9E1' : '#707070'}
        fontSize={14}
      >
        <tspan x={x - 6} dy={secondLineText ? -4 : 4}>{t1}</tspan>
        {secondLineText && (
          <tspan x={x - 6} dy={14}>{t2}</tspan>
        )}
      </text>
    );
  };

  return (
    <>
      <style>
        {`
          .chart-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: #eceffb transparent;
          }
          .dark .chart-scrollbar {
            scrollbar-color: #454448 transparent;
          }
          .chart-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          .chart-scrollbar::-webkit-scrollbar-track {
            background: transparent;
          }
          .chart-scrollbar::-webkit-scrollbar-thumb {
            background: #eceffb;
            border-radius: 999px;
          }
          .dark .chart-scrollbar::-webkit-scrollbar-thumb {
            background: #454448;
            border-radius: 999px;
          }
        `}
      </style>      
      <div className="dark:bg-gradient-to-r from-[#232142]/10 to-[#7E64A3]/10 bg-bgSecondary rounded-xl p-4">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div className="flex items-center gap-3">
            <LineChartIcon className="w-5 h-5 dark:text-[#AEB9E1] text-[#707070]" />
            <h2 className="text-[#707070] dark:text-[#AEB9E1] text-lg font-medium tracking-wide">
              {title}
            </h2>
          </div>
          {/* <button className="flex items-center gap-2 px-4 py-2 bg-[#2D2D3F] text-gray-300 hover:text-white rounded-full text-sm font-medium hover:bg-[#3D3D52] transition-colors">
                    Filter
                    <ChevronDown className="w-4 h-4" />
                </button> */}
        </div>

        {/* Chart Area */}
        <div 
          className={`relative w-full chart-scrollbar ${isScrollable ? 'overflow-y-auto' : ''}`} 
          style={{ height: 260 }}
        >
          <div
            style={{
              width: '100%',
              height: isScrollable ? chartData.length * 40 : '100%',
              position: 'relative',
            }}
          >
            {!hasData && (
              <div className="absolute inset-0 flex items-center justify-center z-10 backdrop-blur-[2px]">
                <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm px-4 py-2 rounded-md border border-gray-200 dark:border-gray-700/50 shadow-sm">
                  <span className="text-gray-500 dark:text-gray-400 text-lg font-medium">
                    No data to display
                  </span>
                </div>
              </div>
            )}
            <div
              className={`h-full w-full ${!hasData ? 'opacity-50 pointer-events-none' : ''}`}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  layout="vertical"
                  data={chartData}
                  margin={{ top: 10, right: 50, left: 20, bottom: 5 }}
                  barSize={isScrollable ? 18 : 25}  // ⭐ Conditional thickness
                  onClick={(data) => {
                    if (hasData && data && data.activePayload && data.activePayload.length > 0) {
                      onBarClick(data.activePayload[0].payload);
                    }
                  }}
                  style={{ cursor: hasData ? 'pointer' : 'default' }}
                >
                  <CartesianGrid
                    strokeDasharray="4 4"
                    stroke={isDark ? '#34353CA3' : '#E0E0E0'}
                  />
                  <XAxis
                    type="number"
                    hide={true}  // ⭐ Hidden — we render custom labels below the scroll area
                    domain={[0, customMax]}
                    ticks={customTicks}
                    allowDecimals={false}
                  />
                  <YAxis
                    type="category"   // ⭐ IMPORTANT
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    width={Y_AXIS_WIDTH}   // ⭐ Space for labels
                    tick={<CustomYAxisTick />}
                  />
                  <Tooltip
                    cursor={{ fill: 'rgba(0, 0, 0, 0.1)' }}
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                      borderRadius: '4px',
                      border: '1px solid #E5E7EB',
                    }}
                    itemStyle={{ fontSize: '12px', color: '#374151', padding: '2px 0' }}
                    labelStyle={{
                      fontSize: '13px',
                      fontWeight: '700',
                      marginBottom: '4px',
                      color: '#707070',
                    }}
                  />
                  {chartKeys.map((key, index) => (
                    <Bar
                      key={key}
                      dataKey={key}
                      stackId="a"
                      fill={chartColors[index % chartColors.length]}
                    >
                      {index === chartKeys.length - 1
                        // <LabelList
                        //   dataKey="name"
                        //   content={<StackBottomLabel />}
                        //   position="bottom"
                        //   offset={10}
                        //   fill="#707070"
                        //   fontSize={8}
                        // />
                      }
                    </Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Custom X-Axis Count Labels — always visible below scroll area */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            paddingLeft: Y_AXIS_WIDTH + 10, // YAxis width (120) + Chart left margin (10)
            paddingRight: 50, // Match Chart right margin (50)
            marginTop: 4,
          }}
        >
          <div style={{ flex: 1, position: 'relative', height: 32 }}>
            {/* Axis line */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 1,
                backgroundColor: isDark ? '#AEB9E133' : '#E0E0E0',
              }}
            />
            {/* Tick labels */}
            {customTicks.map((tick) => (
              <span
                key={tick}
                style={{
                  position: 'absolute',
                  left: `${(tick / customMax) * 100}%`,
                  top: 6,
                  transform: 'translateX(-50%)',
                  fontSize: 11,
                  fontWeight: 500,
                  color: isDark ? '#AEB9E1' : '#707070',
                  whiteSpace: 'nowrap',
                }}
              >
                {tick}
              </span>
            ))}
            {/* "Counts" label centered */}
            <span
              style={{
                position: 'absolute',
                left: '50%',
                bottom: -2,
                transform: 'translateX(-50%)',
                fontSize: 11,
                fontWeight: 600,
                color: isDark ? '#AEB9E1' : '#707070',
                letterSpacing: '0.5px',
              }}
            >
              {/* Counts */}
            </span>
          </div>
        </div>

        {/* Stationary Legend below the scroller */}
        <div className="flex flex-wrap justify-center gap-6 mt-4">
          {chartKeys.map((key, index) => (
            <div key={key} className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full shadow-sm"
                style={{
                  backgroundColor: chartColors[index % chartColors.length],
                }}
              />
              <span className="text-[#707070] dark:text-[#AEB9E1] text-sm font-medium">
                {key}
              </span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default StackedBarChart;
